import React from 'react';
import Container from '../components/ui/Container';
import NotebookPanel from '../components/ui/NotebookPanel';
import SafeIcon from '../common/SafeIcon';
import Badge from '../components/ui/Badge';

const ParentGuide = () => {
  return (
    <div className="bg-slate-50 min-h-screen pt-40 pb-24">
      <Container>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="blue" className="mb-4">THE LABORATORY MANUAL</Badge>
            <h1 className="text-5xl md:text-7xl font-black text-ink-navy mb-6 italic uppercase">Parent Guide</h1>
            <p className="text-xl text-slate-500 font-bold">A manifesto for curious families and intentional play.</p>
          </div>

          <div className="space-y-16">
            <NotebookPanel title="1. Our Core Philosophy" annotation="Read First!">
              <div className="space-y-6 text-lg leading-relaxed">
                <p>
                  At LittleLab STEM, we don't sell "toys." we provide <strong>platforms for discovery.</strong> We believe that every child is born with the innate ability to observe, hypothesize, and experiment.
                </p>
                <p>
                  Our role is simply to provide the high-quality, safe materials that allow that natural curiosity to flourish without frustration.
                </p>
                <div className="grid md:grid-cols-2 gap-6 pt-6">
                  <div className="bg-sky-light/30 p-6 rounded-2xl border-2 border-sky-blue/20">
                    <h4 className="font-black text-ink-navy mb-2 flex items-center gap-2">
                      <SafeIcon name="CheckCircle" className="text-sky-blue" /> Intentionality
                    </h4>
                    <p className="text-sm font-semibold text-slate-600">Every kit is designed with a specific learning outcome, from motor skills to logical flow.</p>
                  </div>
                  <div className="bg-lemon-yellow/10 p-6 rounded-2xl border-2 border-lemon-yellow/20">
                    <h4 className="font-black text-ink-navy mb-2 flex items-center gap-2">
                      <SafeIcon name="CheckCircle" className="text-lemon-bright" /> Realistic Science
                    </h4>
                    <p className="text-sm font-semibold text-slate-600">We use real concepts and materials, not "magic" tricks. Authenticity builds real confidence.</p>
                  </div>
                </div>
              </div>
            </NotebookPanel>

            <div className="bg-ink-navy rounded-[40px] border-4 border-ink-navy p-10 md:p-16 text-white shadow-2xl relative overflow-hidden">
               <div className="absolute -right-10 -bottom-10 opacity-10">
                 <SafeIcon name="Shield" className="text-[250px]" />
               </div>
               <div className="relative z-10">
                 <div className="flex items-center gap-4 mb-8">
                   <div className="w-16 h-16 bg-lemon-yellow rounded-2xl flex items-center justify-center border-2 border-white shadow-lg">
                     <SafeIcon name="ShieldCheck" className="text-3xl text-ink-navy" />
                   </div>
                   <h3 className="text-3xl md:text-4xl font-black italic uppercase">The Safety Protocol</h3>
                 </div>
                 <div className="grid md:grid-cols-2 gap-12">
                   <div className="space-y-6">
                     <p className="text-slate-300 font-bold leading-relaxed">
                       Safety is not a feature; it is our foundation. We select materials that are high-quality, durable, and where possible, non-toxic and biodegradable.
                     </p>
                     <p className="text-slate-300 font-bold leading-relaxed">
                       However, real science involves interaction with the physical world. Some kits include small parts, mild reagents, or tools that require precision.
                     </p>
                   </div>
                   <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
                     <h4 className="font-black text-lemon-yellow uppercase tracking-widest mb-4">Mandatory Guidelines:</h4>
                     <ul className="space-y-4">
                       {[
                         "Always read the 'Lab Safety' card first.",
                         "Adult supervision is required for all kits.",
                         "Maintain a designated 'Lab Space' for projects.",
                         "Wash hands thoroughly after every experiment."
                       ].map((item, i) => (
                         <li key={i} className="flex gap-3 text-sm font-bold">
                           <span className="text-lemon-yellow">{i+1}.</span> {item}
                         </li>
                       ))}
                     </ul>
                   </div>
                 </div>
               </div>
            </div>

            <NotebookPanel title="2. The Age Guide Explained" annotation="Stage Matching">
               <div className="grid md:grid-cols-3 gap-8 mt-4">
                 {[
                   { age: "3-5", focus: "Sensory & Motor", text: "Big pieces, bright colors, and tactile feedback to build fundamental confidence." },
                   { age: "6-8", focus: "Process & Flow", text: "Introduction to steps, sequences, and basic 'if/then' logic in chemistry and physics." },
                   { age: "9-12+", focus: "Systems & Logic", text: "Complex assembly, basic electronics, and project-based engineering challenges." }
                 ].map((stage) => (
                   <div key={stage.age} className="border-t-2 border-slate-100 pt-6">
                     <Badge variant="outline" className="mb-3">Stage {stage.age}</Badge>
                     <h4 className="font-black text-ink-navy mb-2">{stage.focus}</h4>
                     <p className="text-xs font-bold text-slate-500 leading-relaxed">{stage.text}</p>
                   </div>
                 ))}
               </div>
            </NotebookPanel>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParentGuide;